import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in ms

// Idle expiry is decided by comparing wall-clock times against a stored stamp,
// never by a long setTimeout. A background tab gets its timers throttled or
// frozen outright, so a countdown silently loses time and the deadline is never
// reached; a timestamp cannot. It also has to survive a reload, which an
// in-memory timer does not.
const ACTIVITY_KEY = 'lastActivity';
const ACTIVITY_WRITE_INTERVAL = 30 * 1000; // throttle: don't write on every mousemove
const IDLE_CHECK_INTERVAL = 30 * 1000;

// Browser storage throws in a private window or with site data blocked; a bad
// accessor must not take the whole auth context down with it.
const readLastActivity = () => {
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY);
    const ts = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
};

const writeLastActivity = (ts) => {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(ts));
  } catch {
    /* non-persistent session: the in-tab checks still work */
  }
};

const isIdleExpired = () => {
  const last = readLastActivity();
  return last !== null && Date.now() - last > INACTIVITY_TIMEOUT;
};

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRole] = useState(null);
  const lastWriteRef = useRef(0); // throttles activity writes
  const tokenRef = useRef(token);

  useEffect(() => { tokenRef.current = token; }, [token]);

  const realUser = authUser;
  const user = (viewAsRole && authUser?.role === 'admin')
    ? { ...authUser, role: viewAsRole }
    : authUser;

  const clearSession = useCallback(() => {
    setToken(null);
    setAuthUser(null);
    setViewAsRole(null);
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem(ACTIVITY_KEY);
    } catch { /* nothing to clear */ }
  }, []);

  // Start/stop inactivity tracking based on login state
  useEffect(() => {
    if (!token) return;

    const markActivity = () => {
      const now = Date.now();
      if (now - lastWriteRef.current >= ACTIVITY_WRITE_INTERVAL) {
        lastWriteRef.current = now;
        writeLastActivity(now);
      }
    };

    const expireIfIdle = () => {
      if (!tokenRef.current) return;
      if (isIdleExpired()) {
        try {
          sessionStorage.setItem('loggedOutReason', 'inactivity');
        } catch { /* the toast is a nicety, not a requirement */ }
        clearSession();
      }
    };

    const onVisibility = () => {
      // The case a countdown could never catch: a frozen background tab.
      if (document.visibilityState === 'visible') expireIfIdle();
    };

    const onStorage = (e) => {
      // Another tab signed out; follow it rather than keep working from the
      // token still held in memory here.
      if (e.key === 'token' && e.newValue === null) clearSession();
    };

    // `capture` matters: scroll events don't bubble, so without it scrolling
    // inside a table or dialog would not count as activity.
    const listenerOpts = { passive: true, capture: true };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, markActivity, listenerOpts));
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    const interval = setInterval(expireIfIdle, IDLE_CHECK_INTERVAL);

    // Stamp on login so a fresh session starts its clock now.
    lastWriteRef.current = Date.now();
    writeLastActivity(lastWriteRef.current);

    return () => {
      events.forEach(e => window.removeEventListener(e, markActivity, listenerOpts));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [token, clearSession]);

  const logout = () => clearSession();

  useEffect(() => {
    let storedToken = null;
    let storedUser = null;
    try {
      storedToken = localStorage.getItem('token');
      storedUser = localStorage.getItem('user');
    } catch { /* treated as no stored session */ }

    if (storedToken && storedUser) {
      // A missing stamp means the session predates this check, so there is no
      // evidence of recent activity — treat it as stale rather than granting a
      // fresh hour, which is the bug this fixes.
      if (readLastActivity() === null || isIdleExpired()) {
        clearSession();
        try {
          sessionStorage.setItem('loggedOutReason', 'inactivity');
        } catch { /* ignore */ }
      } else {
        try {
          setToken(storedToken);
          setAuthUser(JSON.parse(storedUser));
        } catch {
          clearSession();
        }
      }
    }
    setLoading(false);
  }, [clearSession]);

  const login = async (name, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { name, password });
      const { token, role, name: userName } = response.data;
      const userData = { name: userName, role };
      setToken(token);
      setAuthUser(userData);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      return { success: true, role };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (name, email, password, role = 'user', authToken = null) => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : (token ? { Authorization: `Bearer ${token}` } : {});
      await axios.post(`${API}/auth/register`, { name, email, password, role }, { headers });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, realUser, token, login, register, logout, loading, getAuthHeader, viewAsRole, setViewAsRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
